import React, { memo, Suspense, useCallback, useState, useMemo, useRef } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import { Modal } from 'antd'
import { useSelector } from 'react-redux'
import { getVisibleFormData, getHrFormData, getVisibleCancelModal } from '../../../selectors'
import { ModalCreateTask } from 'src/react-app/views/modal-create-task-new'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import { useNavigate } from 'react-router-dom'
import {
  EditOutlined,
  PlusOutlined,
  PrinterOutlined,
  CloseCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { PrintPrepareModal } from '../../../../../../pages_v2/orders/card/ui/modals/modal-print-prepare'
import MessageCopyModal from '../../message-copy-modal'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { HSteps } from '../../../../../../ui/Steps'
import { HrModal } from '../../hr-modal'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { nextStage } from '../../../../../../store/hr/action-creators'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { PrintTemplateSelectModal } from '../../hr-modal-print-template-select'

interface HrFormsHeaderProps {
  id: string | number
  className?: string
  stage?: number
  nextStageOnClick?: () => void
  isDisabled?: boolean
  showTitle?: boolean
}

interface ITemplate {
  id: number
  name: string
  file_name?: string
  message?: string
}

const titlesButton = ['Взять в работу', 'Назначить собеседование', 'Оформление', 'Принят на работу']

export const HrFormsHeader: React.FC<HrFormsHeaderProps> = memo((props: HrFormsHeaderProps) => {
  const { id, stage = 0, nextStageOnClick, isDisabled } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [isOpenModalCreateTask, setIsOpenModalCreateTask] = useState<boolean>(false)
  const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
  const [isOpenCancelModal, setIsOpenCancelModal] = useState<boolean>(false)
  const [isOpenTemplateSelectModal, setIsOpenTemplateSelectModal] = useState<boolean>(false)

  const isDisabledButtons = id === 'new'
  const visibleFormData = useSelector(getVisibleFormData)
  const hrFormData = useSelector(getHrFormData)
  const visibleCancelModal = useSelector(getVisibleCancelModal)
  const isVisibleCancelButton =
    visibleCancelModal &&
    hrFormData?.stages?.items &&
    hrFormData?.stages?.current < Object.values(hrFormData?.stages?.items).length - 1

  const templates: ITemplate[] = visibleFormData?.templates || []
  const [template, setTemplate] = useState<null | ITemplate>(null)
  const formRef = useRef<any>(
    visibleCancelModal && typeof visibleCancelModal === 'object' ? { ...visibleCancelModal } : null,
  )

  React.useEffect(() => {
    if (visibleCancelModal && typeof visibleCancelModal === 'object') {
      formRef.current = { ...visibleCancelModal }
    }
  }, [visibleCancelModal])

  const handleNextStageOnClick = useCallback(async () => {
    if (nextStageOnClick) {
      await nextStageOnClick?.()
    }
  }, [nextStageOnClick])

  const onCloseAddNote = useCallback(() => {
    setIsOpenModalAddNote(false)
  }, [])

  const onCloseCreateTask = useCallback(() => {
    setIsOpenModalCreateTask(false)
  }, [])

  const openPrepareModal = useCallback((tpl: ITemplate) => {
    setTemplate(tpl)
  }, [])

  const onClosePrint = useCallback(() => {
    setTemplate(null)
  }, [])

  const handleCancelClick = useCallback(() => {
    navigate('/hr')
  }, [navigate])

  const handleCancelButtonClick = useCallback(() => {
    setIsOpenCancelModal(true)
  }, [])

  const handleCloseCancelModal = useCallback(() => {
    setIsOpenCancelModal(false)
  }, [])

  const onFormDataModal = useCallback(async ({ formData }) => {
    if (!formRef.current) {
      return
    }
    const isFilesEdit = JSON.stringify(formData) === JSON.stringify(formRef.current?.fields)
    if (isFilesEdit) {
      return
    }
    formRef.current.fields = { ...formData }
  }, [])

  const handleOkCancelModal = useCallback(async () => {
    if (!formRef.current) {
      return
    }
    const formData = { ...formRef.current?.fields }
    const isRequiredFields = isFormDataRequired(formData, formRef.current.schema)

    if (!isRequiredFields) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните обязательные поля',
      })
      return
    }
    dispatch(nextStage(id, formData))
    setIsOpenCancelModal(false)
  }, [dispatch, id])

  const handleTemplateSelect = useCallback((selectedTemplate: ITemplate) => {
    setTemplate(selectedTemplate)
  }, [])

  const handleMobileDropdownAction = useCallback((actionKey: string) => {
    if (actionKey === 'print') {
      setIsOpenTemplateSelectModal(true)
    }
  }, [])

  const printMenuItems = useMemo(() => {
    return templates.map(tpl => ({
      key: tpl.id.toString(),
      label: tpl.name,
      onClick: () => openPrepareModal(tpl),
    }))
  }, [templates, openPrepareModal])

  const actionsConfig = useMemo(
    (): CardActionConfig[] => [
      {
        key: 'next-stage',
        label: titlesButton[stage] || '',
        type: 'primary',
        onClick: handleNextStageOnClick,
        isPrimary: true,
        condition: Boolean(titlesButton[stage]),
        disabled: isDisabledButtons || isDisabled,
        loading: isDisabled,
        order: 1,
      },
      {
        key: 'add-note',
        label: 'Добавить заметку',
        icon: <EditOutlined />,
        onClick: () => setIsOpenModalAddNote(true),
        condition: !isDisabledButtons,
        order: 10,
      },
      {
        key: 'create-task',
        label: 'Создать задачу',
        icon: <PlusOutlined />,
        onClick: () => setIsOpenModalCreateTask(true),
        condition: !isDisabledButtons,
        order: 20,
      },
      {
        key: 'print',
        label: 'Печать',
        icon: <PrinterOutlined />,
        type: 'dropdown',
        condition: templates.length > 0,
        menuItems: printMenuItems,
        order: 30,
      },
      {
        key: 'cancel-record',
        label:
          visibleCancelModal && typeof visibleCancelModal === 'object'
            ? visibleCancelModal.title
            : 'Отмена',
        icon: <CloseOutlined />,
        danger: true,
        onClick: handleCancelButtonClick,
        disabled: isDisabled,
        condition: isVisibleCancelButton,
        order: 90,
      },
      {
        key: 'cancel',
        label: 'Отмена',
        icon: <CloseCircleOutlined />,
        onClick: handleCancelClick,
        danger: true,
        condition: isDisabledButtons,
        order: 100,
      },
    ],
    [
      stage,
      isDisabledButtons,
      handleNextStageOnClick,
      templates,
      printMenuItems,
      handleCancelClick,
      isVisibleCancelButton,
      visibleCancelModal,
      handleCancelButtonClick,
      isDisabled,
    ],
  )

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  return (
    <>
      <HStack gap={'8'} className={'mapWrapper'} wrap>
        <div>
          {hrFormData?.stages && (
            <HSteps
              items={Object.values(hrFormData?.stages?.items)}
              current={hrFormData?.stages?.current}
              history={hrFormData?.stages?.history}
            />
          )}
        </div>
        <CardActions
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
          onMobileDropdownAction={handleMobileDropdownAction}
        />
      </HStack>

      <Suspense fallback={''}>
        <ModalCreateTask
          isOpen={isOpenModalCreateTask}
          onClose={onCloseCreateTask}
          entityType={'hr'}
          entityId={id}
        />
      </Suspense>

      <ModalAddNote
        entityId={Number(id)}
        entityType={'hr'}
        isOpen={isOpenModalAddNote}
        onClose={onCloseAddNote}
      />

      {visibleCancelModal &&
        typeof visibleCancelModal === 'object' &&
        visibleCancelModal.fields && (
          <HrModal
            schema={visibleCancelModal.schema}
            onChange={onFormDataModal}
            formData={visibleCancelModal.fields}
            isOpen={isOpenCancelModal}
            onCancel={handleCloseCancelModal}
            onOk={handleOkCancelModal}
            centered={true}
            disabled={isDisabled}
            title={visibleCancelModal.title}
          />
        )}

      {template && template?.message && (
        <MessageCopyModal id={id} template={template} onClose={onClosePrint} />
      )}

      {template && template?.file_name && (
        <PrintPrepareModal
          isModalPrintOpen={true}
          onCancel={onClosePrint}
          template={template}
          id={id}
        />
      )}

      <PrintTemplateSelectModal
        isOpen={isOpenTemplateSelectModal}
        templates={templates}
        onCancel={() => setIsOpenTemplateSelectModal(false)}
        onSelectTemplate={handleTemplateSelect}
      />
    </>
  )
})

HrFormsHeader.displayName = 'HrFormsHeader'
